import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  adminApi,
  ADMIN_TOKEN_KEY,
  ADMIN_USER_KEY,
  ADMIN_TENANT_KEY,
  getApiErrorMessage,
} from '../services/adminApi'

const AdminAuthContext = createContext(null)

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(() => {
    try {
      const raw = localStorage.getItem(ADMIN_USER_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY))

  const persist = useCallback((token, adminData) => {
    localStorage.setItem(ADMIN_TOKEN_KEY, token)
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(adminData))
    if (adminData.tenant_id) {
      localStorage.setItem(ADMIN_TENANT_KEY, adminData.tenant_id)
    } else {
      localStorage.removeItem(ADMIN_TENANT_KEY)
    }
    setAdmin(adminData)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY)
    localStorage.removeItem(ADMIN_USER_KEY)
    localStorage.removeItem(ADMIN_TENANT_KEY)
    setAdmin(null)
  }, [])

  const refreshMe = useCallback(async () => {
    const { data } = await adminApi.me()
    const next = data.admin
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(next))
    setAdmin(next)
    return next
  }, [])

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY)
    if (!token) {
      setLoading(false)
      return
    }
    refreshMe()
      .catch(() => logout())
      .finally(() => setLoading(false))
  }, [refreshMe, logout])

  const login = useCallback(
    async (email, password) => {
      const { data } = await adminApi.login(email, password)
      persist(data.token, data.admin)
      return data.admin
    },
    [persist]
  )

  const value = useMemo(
    () => ({
      admin,
      loading,
      isAuthenticated: !!admin && !!localStorage.getItem(ADMIN_TOKEN_KEY),
      isMaster: ['super_admin', 'support'].includes(admin?.role),
      isSuperAdmin: admin?.role === 'super_admin',
      isRevenda: admin?.role === 'revenda',
      login,
      logout,
      refreshMe,
      getApiErrorMessage,
    }),
    [admin, loading, login, logout, refreshMe]
  )

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth deve ser usado dentro de AdminAuthProvider')
  return ctx
}
